import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaChartBar, 
  FaTable, FaFilter, FaDownload, FaRedo,
  FaEyeSlash, FaExpand, FaCompress, FaShare,
  FaArrowUp, FaArrowDown, FaMinus
} from 'react-icons/fa';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  RadialLinearScale,
  TimeScale,
  Filler
} from 'chart.js';
import { Line, Bar, Doughnut, Scatter, Radar, PolarArea } from 'react-chartjs-2';

// تسجيل مكونات Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  RadialLinearScale,
  TimeScale,
  Filler
);

// أنواع البيانات
interface DashboardMetric {
  id: string;
  title: string;
  value: string | number;
  change?: number;
  changeType?: 'increase' | 'decrease' | 'neutral';
  icon?: string;
  color?: string;
  format?: 'number' | 'currency' | 'percentage' | 'text';
  precision?: number;
}

interface DashboardChart {
  id: string;
  title: string;
  type: 'bar' | 'line' | 'pie' | 'doughnut' | 'scatter' | 'bubble' | 'radar' | 'polarArea' | 'area' | 'horizontalBar';
  data: {
    labels: string[];
    datasets: {
      label: string;
      data: number[];
      backgroundColor?: string | string[];
      borderColor?: string | string[];
      borderWidth?: number;
      fill?: boolean;
      tension?: number;
      pointRadius?: number;
      pointHoverRadius?: number;
    }[];
  };
  options?: any;
  height?: string;
  width?: string;
  responsive?: boolean;
  gridPosition?: {
    x: number;
    y: number;
    w: number;
    h: number;
  };
}

interface DashboardFilter {
  id: string;
  name: string;
  type: 'select' | 'multiselect' | 'date' | 'daterange' | 'number' | 'text' | 'boolean';
  options?: any[];
  value?: any;
  defaultValue?: any;
  placeholder?: string;
  required?: boolean;
}

interface DashboardData {
  metrics: DashboardMetric[];
  charts: DashboardChart[];
  filters: DashboardFilter[];
  timeRange?: {
    start: Date;
    end: Date;
    granularity: 'hour' | 'day' | 'week' | 'month' | 'quarter' | 'year';
  };
  refreshRate?: number;
}

interface AdvancedDashboardProps {
  data: string;
  currentLanguage: 'ar' | 'en';
  onExport?: (data: any) => void;
  onShare?: (data: any) => void;
}

// مكون المقاييس
function MetricCard({ metric, currentLanguage }: { metric: DashboardMetric; currentLanguage: 'ar' | 'en' }) {
  const formatValue = (value: string | number, format?: string, precision?: number) => {
    if (format === 'percentage') {
      return `${Number(value).toFixed(precision || 1)}%`;
    } else if (format === 'currency') {
      return new Intl.NumberFormat(currentLanguage === 'ar' ? 'ar-SA' : 'en-US', {
        style: 'currency',
        currency: 'SAR'
      }).format(Number(value));
    } else if (format === 'number') {
      return new Intl.NumberFormat(currentLanguage === 'ar' ? 'ar-SA' : 'en-US').format(Number(value));
    }
    return value;
  };

  const getChangeIcon = (changeType?: string) => {
    switch (changeType) {
      case 'increase':
        return <FaArrowUp className="text-green-500" />;
      case 'decrease':
        return <FaArrowDown className="text-red-500" />;
      default:
        return <FaMinus className="text-gray-500" />;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-gray-700"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3 rtl:space-x-reverse">
          <div 
            className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl"
            style={{ backgroundColor: metric.color + '20' }}
          >
            {metric.icon}
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
              {metric.title}
            </h3>
            <div className="flex items-center space-x-2 rtl:space-x-reverse">
              <span className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatValue(metric.value, metric.format, metric.precision)}
              </span>
              {metric.change !== undefined && (
                <div className="flex items-center space-x-1 rtl:space-x-reverse">
                  {getChangeIcon(metric.changeType)}
                  <span className={`text-sm font-medium ${
                    metric.changeType === 'increase' ? 'text-green-600' :
                    metric.changeType === 'decrease' ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {metric.change > 0 ? '+' : ''}{metric.change}%
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// مكون الرسم البياني
function ChartWidget({ chart, currentLanguage }: { chart: DashboardChart; currentLanguage: 'ar' | 'en' }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  const renderChart = () => {
    const chartData = {
      labels: chart.data.labels,
      datasets: chart.data.datasets.map(dataset => ({
        ...dataset,
        backgroundColor: dataset.backgroundColor || '#8B5CF6',
        borderColor: dataset.borderColor || '#7C3AED',
      }))
    };

    const options = {
      ...chart.options,
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        ...chart.options?.plugins,
        legend: {
          ...chart.options?.plugins?.legend,
          labels: {
            color: currentLanguage === 'ar' ? '#374151' : '#374151',
            font: { size: 12 }
          }
        }
      }
    };

    switch (chart.type) {
      case 'bar':
        return <Bar data={chartData} options={options} />;
      case 'line':
        return <Line data={chartData} options={options} />;
      case 'doughnut':
      case 'pie':
        return <Doughnut data={chartData} options={options} />;
      case 'scatter':
        return <Scatter data={chartData} options={options} />;
      case 'radar':
        return <Radar data={chartData} options={options} />;
      case 'polarArea':
        return <PolarArea data={chartData} options={options} />;
      default:
        return <Bar data={chartData} options={options} />;
    }
  };

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 ${
        isExpanded ? 'fixed inset-4 z-50' : 'relative'
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {chart.title}
        </h3>
        <div className="flex items-center space-x-2 rtl:space-x-reverse">
          <button
            onClick={() => setIsVisible(false)}
            className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            title={currentLanguage === 'ar' ? 'إخفاء' : 'Hide'}
          >
            <FaEyeSlash className="w-4 h-4" />
          </button>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            title={currentLanguage === 'ar' ? 'تكبير' : 'Expand'}
          >
            {isExpanded ? <FaCompress className="w-4 h-4" /> : <FaExpand className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Chart Content */}
      <div className={`p-4 ${isExpanded ? 'h-full' : 'h-80'}`}>
        {renderChart()}
      </div>

      {/* Expanded Overlay */}
      {isExpanded && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setIsExpanded(false)} />
      )}
    </motion.div>
  );
}

// مكون الفلاتر
function FilterPanel({ filters, onFilterChange, currentLanguage }: { 
  filters: DashboardFilter[]; 
  onFilterChange: (filterId: string, value: any) => void;
  currentLanguage: 'ar' | 'en';
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 rtl:space-x-reverse px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
      >
        <FaFilter className="w-4 h-4" />
        <span>{currentLanguage === 'ar' ? 'الفلاتر' : 'Filters'}</span>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full left-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50"
          >
            <div className="p-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {currentLanguage === 'ar' ? 'الفلاتر المتاحة' : 'Available Filters'}
              </h3>
              
              <div className="space-y-4">
                {filters.map(filter => (
                  <div key={filter.id} className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {filter.name}
                    </label>
                    
                    {filter.type === 'select' && (
                      <select
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        value={filter.value || ''}
                        onChange={(e) => onFilterChange(filter.id, e.target.value)}
                      >
                        <option value="">{filter.placeholder}</option>
                        {filter.options?.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    )}

                    {filter.type === 'multiselect' && (
                      <div className="space-y-2">
                        {filter.options?.map(option => (
                          <label key={option.value} className="flex items-center space-x-2 rtl:space-x-reverse">
                            <input
                              type="checkbox"
                              checked={filter.value?.includes(option.value) || false}
                              onChange={(e) => {
                                const currentValues = filter.value || [];
                                const newValues = e.target.checked
                                  ? [...currentValues, option.value]
                                  : currentValues.filter((v: any) => v !== option.value);
                                onFilterChange(filter.id, newValues);
                              }}
                              className="rounded border-gray-300 dark:border-gray-600"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {option.label}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}

                    {filter.type === 'number' && (
                      <input
                        type="number"
                        placeholder={filter.placeholder}
                        value={filter.value || ''}
                        onChange={(e) => onFilterChange(filter.id, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    )}

                    {filter.type === 'text' && (
                      <input
                        type="text"
                        placeholder={filter.placeholder}
                        value={filter.value || ''}
                        onChange={(e) => onFilterChange(filter.id, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// المكون الرئيسي
export default function AdvancedDashboard({ data, currentLanguage, onExport, onShare }: AdvancedDashboardProps) {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: any }>({});
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [autoRefresh, setAutoRefresh] = useState(false);

  // تحليل البيانات وإنشاء لوحة التحكم
  useEffect(() => {
    if (!data) {
      setDashboardData(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    
    try {
      // هنا سيتم استدعاء دالة تحليل البيانات المتقدمة
      const parsedData = JSON.parse(data);
      const analysis = analyzeDataStructure(parsedData);
      
      const dashboard: DashboardData = {
        metrics: createMetrics(parsedData, analysis),
        charts: createCharts(parsedData, analysis),
        filters: createFilters(parsedData, analysis),
        refreshRate: 30000
      };

      setDashboardData(dashboard);
    } catch (error) {
      console.error('Error parsing data:', error);
      setDashboardData(null);
    } finally {
      setIsLoading(false);
    }
  }, [data, currentLanguage]);

  // التحديث التلقائي
  useEffect(() => {
    if (!autoRefresh || !dashboardData) return;

    const interval = setInterval(() => {
      // تحديث البيانات
      console.log('Auto refreshing dashboard...');
    }, dashboardData.refreshRate);

    return () => clearInterval(interval);
  }, [autoRefresh, dashboardData]);

  // دوال مساعدة
  const analyzeDataStructure = (data: any[]) => {
    if (data.length === 0) return {};

    const firstRow = data[0];
    const columns = Object.keys(firstRow);
    const analysis: any = {
      columns: {},
      numericColumns: [],
      categoricalColumns: [],
      dateColumns: [],
      totalRecords: data.length
    };

    columns.forEach((column: string) => {
      const values = data.map((row: any) => row[column]).filter((val: any) => val !== null && val !== undefined);
      const isNumeric = values.every(val => typeof val === 'number' || !isNaN(Number(val)));
      const isDate = values.every(val => !isNaN(Date.parse(val)));

      if (isNumeric) {
        analysis.numericColumns.push(column);
      } else if (isDate) {
        analysis.dateColumns.push(column);
      } else {
        analysis.categoricalColumns.push(column);
      }
    });

    return analysis;
  };

  const createMetrics = (data: any[], analysis: any): DashboardMetric[] => {
    return [
      {
        id: 'total-records',
        title: currentLanguage === 'ar' ? 'إجمالي السجلات' : 'Total Records',
        value: data.length,
        icon: '📊',
        color: '#8B5CF6',
        format: 'number'
      },
      {
        id: 'total-columns',
        title: currentLanguage === 'ar' ? 'إجمالي الأعمدة' : 'Total Columns',
        value: Object.keys(data[0] || {}).length,
        icon: '📋',
        color: '#3B82F6',
        format: 'number'
      },
      {
        id: 'numeric-columns',
        title: currentLanguage === 'ar' ? 'المتغيرات الرقمية' : 'Numeric Columns',
        value: analysis.numericColumns?.length || 0,
        icon: '🔢',
        color: '#F59E0B',
        format: 'number'
      },
      {
        id: 'categorical-columns',
        title: currentLanguage === 'ar' ? 'المتغيرات الفئوية' : 'Categorical Columns',
        value: analysis.categoricalColumns?.length || 0,
        icon: '🏷️',
        color: '#EC4899',
        format: 'number'
      }
    ];
  };

  const createCharts = (data: any[], analysis: any): DashboardChart[] => {
    const charts: DashboardChart[] = [];

    // رسم بياني للأعمدة للمتغيرات الرقمية
    if (analysis.numericColumns && analysis.numericColumns.length > 0) {
      const column = analysis.numericColumns[0];
      const values = data.map((row: any) => row[column]).filter((val: any) => typeof val === 'number');
      
      if (values.length > 0) {
        charts.push({
          id: 'numeric-distribution',
          title: currentLanguage === 'ar' ? `توزيع ${column}` : `Distribution of ${column}`,
          type: 'bar',
          data: {
            labels: ['0-25%', '25-50%', '50-75%', '75-100%'],
            datasets: [{
              label: column,
              data: [25, 25, 25, 25], // مثال بسيط
              backgroundColor: '#8B5CF6',
              borderColor: '#7C3AED',
              borderWidth: 1
            }]
          },
          options: {
            responsive: true,
            plugins: {
              title: {
                display: true,
                text: currentLanguage === 'ar' ? `توزيع ${column}` : `Distribution of ${column}`,
                font: { size: 16, weight: 'bold' }
              }
            }
          }
        });
      }
    }

    // رسم بياني دائري للمتغيرات الفئوية
    if (analysis.categoricalColumns && analysis.categoricalColumns.length > 0) {
      const column = analysis.categoricalColumns[0];
      const counts: { [key: string]: number } = {};
      
      data.forEach((row: any) => {
        const value = String(row[column] || 'غير محدد');
        counts[value] = (counts[value] || 0) + 1;
      });

      const sorted = Object.entries(counts)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5);

      if (sorted.length > 0) {
        charts.push({
          id: 'categorical-distribution',
          title: currentLanguage === 'ar' ? `توزيع ${column}` : `Distribution of ${column}`,
          type: 'doughnut',
          data: {
            labels: sorted.map(([label]) => label),
            datasets: [{
              label: column,
              data: sorted.map(([, count]) => count),
              backgroundColor: ['#8B5CF6', '#EC4899', '#10B981', '#F59E0B', '#EF4444']
            }]
          },
          options: {
            responsive: true,
            plugins: {
              title: {
                display: true,
                text: currentLanguage === 'ar' ? `توزيع ${column}` : `Distribution of ${column}`,
                font: { size: 16, weight: 'bold' }
              }
            }
          }
        });
      }
    }

    return charts;
  };

  const createFilters = (data: any[], analysis: any): DashboardFilter[] => {
    const filters: DashboardFilter[] = [];

    if (analysis.categoricalColumns && analysis.categoricalColumns.length > 0) {
      analysis.categoricalColumns.slice(0, 3).forEach((column: string) => {
        const uniqueValues = [...new Set(data.map((row: any) => row[column]))];
        if (uniqueValues.length <= 10) {
          filters.push({
            id: `filter-${column}`,
            name: column,
            type: 'multiselect',
            options: uniqueValues.map((value: any) => ({ label: String(value), value })),
            placeholder: currentLanguage === 'ar' ? `اختر ${column}` : `Select ${column}`
          });
        }
      });
    }

    return filters;
  };

  const handleFilterChange = useCallback((filterId: string, value: any) => {
    setActiveFilters(prev => ({
      ...prev,
      [filterId]: value
    }));
  }, []);

  const handleExport = useCallback(() => {
    if (onExport && dashboardData) {
      onExport(dashboardData);
    }
  }, [onExport, dashboardData]);

  const handleShare = useCallback(() => {
    if (onShare && dashboardData) {
      onShare(dashboardData);
    }
  }, [onShare, dashboardData]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">
            {currentLanguage === 'ar' ? 'جاري تحميل لوحة التحكم...' : 'Loading dashboard...'}
          </p>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 dark:text-gray-400">
          {currentLanguage === 'ar' ? 'لا توجد بيانات متاحة' : 'No data available'}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 rtl:space-x-reverse">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {currentLanguage === 'ar' ? 'لوحة التحكم المتقدمة' : 'Advanced Dashboard'}
              </h1>
                              <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <button
                    onClick={() => setAutoRefresh(!autoRefresh)}
                    className={`p-2 rounded-lg transition-colors ${
                      autoRefresh 
                        ? 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-400' 
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                    }`}
                    title={currentLanguage === 'ar' ? 'التحديث التلقائي' : 'Auto Refresh'}
                  >
                    <FaRedo className={`w-4 h-4 ${autoRefresh ? 'animate-spin' : ''}`} />
                  </button>
                </div>
            </div>

            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              <FilterPanel 
                filters={dashboardData.filters} 
                onFilterChange={handleFilterChange}
                currentLanguage={currentLanguage}
              />
              
              <button
                onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                className="p-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                title={currentLanguage === 'ar' ? 'تغيير العرض' : 'Toggle View'}
              >
                {viewMode === 'grid' ? <FaTable className="w-4 h-4" /> : <FaChartBar className="w-4 h-4" />}
              </button>

              <button
                onClick={handleExport}
                className="p-2 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                title={currentLanguage === 'ar' ? 'تصدير' : 'Export'}
              >
                <FaDownload className="w-4 h-4" />
              </button>

              <button
                onClick={handleShare}
                className="p-2 bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400 rounded-lg hover:bg-green-200 dark:hover:bg-green-800 transition-colors"
                title={currentLanguage === 'ar' ? 'مشاركة' : 'Share'}
              >
                <FaShare className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {dashboardData.metrics.map(metric => (
            <MetricCard 
              key={metric.id} 
              metric={metric} 
              currentLanguage={currentLanguage}
            />
          ))}
        </div>

        {/* Charts Grid */}
        <div className={`grid gap-6 ${
          viewMode === 'grid' 
            ? 'grid-cols-1 lg:grid-cols-2' 
            : 'grid-cols-1'
        }`}>
          {dashboardData.charts.map(chart => (
            <ChartWidget 
              key={chart.id} 
              chart={chart} 
              currentLanguage={currentLanguage}
            />
          ))}
        </div>

        {/* Empty State */}
        {dashboardData.charts.length === 0 && (
          <div className="text-center py-12">
            <FaChartBar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {currentLanguage === 'ar' ? 'لا توجد رسوم بيانية متاحة' : 'No charts available'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {currentLanguage === 'ar' 
                ? 'تحقق من البيانات المقدمة لإنشاء الرسوم البيانية' 
                : 'Check the provided data to generate charts'
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
} 